import React, { useState, useMemo } from "react";
import T from '../../../components/common/T';
import { useNavigate } from "react-router-dom";
import { usePurchaseOrder } from "../../../context/PurchaseOrderContext";
import DataTable from "../../../components/common/DataTables.jsx";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../../components/common/PageLayout";
import { TopPrimaryButton } from "../../../components/common/StandardButtons";
import useModuleFilters from "../../../hooks/useModuleFilters";
import {
  ShoppingBagIcon, EyeIcon, ShoppingCartIcon,
  DocumentPlusIcon, CheckCircleIcon, ClockIcon
} from "@heroicons/react/24/outline";

import TableActionButtons from "../../../components/common/TableActionButtons";
import { formatDate } from "../../../utils/dateUtils";

export default function PurchaseList() {
  const navigate = useNavigate();
  const { purchaseRequests = [], deletePR } = usePurchaseOrder();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [projectFilter, setProjectFilter] = useState("All");

  const handleDelete = async (id, prNumber) => {
    if (window.confirm(`Are you sure you want to delete purchase request ${prNumber}?`)) {
      if (deletePR) await deletePR(id);
    }
  };

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("purchase", {
    projects: [],
    statuses: [],
  });

  // --- STATS DASHBOARD CALCULATION ---
  const stats = useMemo(() => [
    {
      label: "Total Requests",
      value: purchaseRequests.length,
      icon: ShoppingCartIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => { setStatusFilter("All"); setProjectFilter("All"); },
    },
    {
      label: "New",
      value: purchaseRequests.filter(p => p.status === "NEW" || p.status === "New").length,
      icon: DocumentPlusIcon,
      variant: "blue",
      isActive: statusFilter === "NEW" || statusFilter === "New",
      onClick: () => setStatusFilter(s => (s === "NEW" || s === "New") ? "All" : "NEW"),
    },
    {
      label: "Pending Approval",
      value: purchaseRequests.filter(p => p.status === "WAITING DEPARTMENT APPROVAL").length,
      icon: ClockIcon,
      variant: "orange",
      isActive: statusFilter === "WAITING DEPARTMENT APPROVAL",
      onClick: () => setStatusFilter(s => s === "WAITING DEPARTMENT APPROVAL" ? "All" : "WAITING DEPARTMENT APPROVAL"),
    },
    {
      label: "PO Created",
      value: purchaseRequests.filter(p => p.status === "PO Created").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "PO Created",
      onClick: () => setStatusFilter(s => s === "PO Created" ? "All" : "PO Created"),
    },
  ], [purchaseRequests, statusFilter]);

  const filteredData = useMemo(() => {
    return purchaseRequests.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.prNumber?.toString() || "").toLowerCase().includes(searchLower) ||
        (item.projectName?.toString() || "").toLowerCase().includes(searchLower) ||
        (item.materialName?.toString() || "").toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === "All" || item.status === statusFilter;
      const matchesProject = projectFilter === "All" || item.projectName === projectFilter || String(item.projectId) === String(projectFilter);
      return matchesSearch && matchesStatus && matchesProject;
    });
  }, [purchaseRequests, searchTerm, statusFilter, projectFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
      case 'New': return 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60';
      case 'WAITING DEPARTMENT APPROVAL': return 'bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800/60';
      case 'Approved': return 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800/60';
      case 'PO Created': return 'bg-green-50 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-800/60';
      default: return 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700';
    }
  };

  const columns = [
    { header: "PR #", accessor: "prNumber", sortable: true, render: (row) => <span className="font-semibold text-purple-700 dark:text-purple-400">{row.prNumber}</span> },
    { header: "Project", accessor: "projectName", sortable: true, render: (row) => <span className="font-medium text-gray-800 dark:text-white">{row.projectName}</span> },
    { header: "Material", accessor: "materialName", sortable: true },
    { header: "Quantity", accessor: "quantity", align: "center", sortable: true, render: (row) => <span className="font-mono text-[12px] bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-2 py-0.5 rounded-md text-gray-700 dark:text-slate-300">{row.quantity}</span> },
    { header: "Required By", accessor: "requiredBy", sortable: true, render: (row) => <span className="text-gray-600 dark:text-slate-400 text-[12px]">{formatDate(row.requiredBy || row.requestDate)}</span> },
    { header: "Status", accessor: "status", align: "center", sortable: true, render: (row) => <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${getStatusColor(row.status)}`}>{row.status}</span> },
    { 
      header: "Actions", 
      align: "right", 
      render: (row) => (
        <TableActionButtons
          onView={() => navigate(`/admin/material/purchase/edit/${row.id}`)}
          onEdit={() => navigate(`/admin/material/purchase/edit/${row.id}`)}
          onDelete={() => handleDelete(row.id, row.prNumber)}
          viewTitle="View Request"
          editTitle="Edit Request"
          deleteTitle="Delete Request"
        />
      )
    }
  ];

  return (
    <PageLayout>
      <PageHeader
        icon={ShoppingCartIcon}
        iconColor="text-[#E8612C]"
        title="Purchase Requests"
        subtitle="Manage material purchase requests and procurement workflow"
        onRefresh={() => window.location.reload()}
      >
        <button
          type="button"
          onClick={() => navigate("/admin/material/purchase-orders")}
          className="h-8 px-3 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] rounded-lg text-[11px] font-bold uppercase hover:bg-gray-50 dark:hover:bg-slate-800 transition shadow-2xs flex items-center gap-1.5 text-gray-700 dark:text-slate-300 cursor-pointer"
        >
          <ShoppingBagIcon className="w-3.5 h-3.5" /> <T>View Orders</T>
        </button>
        <TopPrimaryButton
          onClick={() => navigate("/admin/material/purchase/new")}
          label="Create Request"
        />
      </PageHeader>
      
      {/* Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Unified Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search reference, material, project..."
        tabs={['All', 'NEW', 'WAITING DEPARTMENT APPROVAL', 'Approved', 'PO Created']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setStatusFilter("All"); setProjectFilter("All"); setSearchTerm(""); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Project</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={projectFilter}
            onChange={e => setProjectFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Projects</T></option>
            {(filterOptions.projects || []).map((proj) => (
              <option key={proj.id} value={proj.name}>{proj.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><T>Status</T></label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            <option value="NEW"><T>New</T></option>
            <option value="WAITING DEPARTMENT APPROVAL"><T>Waiting Approval</T></option>
            <option value="Approved"><T>Approved</T></option>
            <option value="PO Created"><T>PO Created</T></option>
          </select>
        </div>
      </FilterPanel>

      {/* Table Card */}
      <TableCard>
        <DataTable data={filteredData} columns={columns} emptyMessage="No requests found" />
      </TableCard>
    </PageLayout>
  );
}